<template>
  <PrimeMultiSelect
    ref="selectRef"
    v-model="modelValue"
    unstyled
    :pt="passThrough"
    :loading="loading"
    :options="filteredOptions"
    :placeholder="placeholder"
    :selectionLimit="selectionLimit"
    :appendTo="appendTo"
    :optionGroupLabel="optionGroupLabel"
    :optionGroupChildren="optionGroupChildren"
    :virtual-scroller-options="primeVirtualScrollerOptions"
    :disabled="disabled"
    :class="[sizeClass, stateClass]"
    class="meridia-select"
    v-bind="$attrs"
    @show="onShow"
    @hide="() => emit('hide')"
    @clear="() => emit('clear')"
    @blur="() => emit('blur')"
    @focus="() => emit('focus')"
  >
    <template #dropdownicon>
      <transition name="fade">
        <MeridiaIcon
          v-if="modelValue?.length > 0 && !clearDisabled"
          class="meridia-select-icon-clear"
          @click="modelValue = undefined"
          name="cancel"
          type="material-icons"
        />
      </transition>
      <MeridiaIcon
        class="meridia-select-dropdown-icon"
        :class="[
          {
            'rotate-180': selectRef?.overlayVisible,
          },
          getPt <
            PassThroughMeridiaMultipleSelect >
            ('dropdownIcon', passThrough)?.class,
        ]"
        name="keyboard_arrow_down"
        type="material-icons"
      />
    </template>
    <template #value="scope">
      <div v-if="slots.value">
        <slot
          name="value"
          v-bind="{
            ...scope,
            modelValue,
            setModelValue: (value) => (modelValue = value),
          }"
        />
      </div>
      <div v-else>
        <div v-if="!(scope.value?.length > 0)">
          {{ scope.placeholder }}
        </div>
        <div
          v-else
          class="whitespace-nowrap flex flex-nowrap gap-2xs overflow-auto scrollbar-thin scrollbar-thumb-slate-400 scrollbar-track-transparent"
        >
          <div v-if="content === SelectContent.TEXT">
            {{ scope.value.map((option) => option.label).join(', ') }}
          </div>
          <div
            v-else
            v-for="(option, index) in scope.value"
            :key="option.value"
          >
            <MeridiaChip
              v-if="content === SelectContent.CHIPS"
              :label="option.label"
              :icon="chipIcon"
              removable
              @remove-clicked.stop="
                () => {
                  if (modelValue) {
                    modelValue = modelValue.filter((_, i) => i !== index);
                    if (modelValue.length === 0) {
                      modelValue = undefined;
                    }
                  }
                }
              "
            />
            <MeridiaTag
              v-else-if="content === SelectContent.TAGS"
              :type="'risk_level'"
              :label="option.label"
              :state="'low'"
            />
          </div>
        </div>
      </div>
    </template>
    <template #header>
      <div
        v-if="!filteringDisabled"
        class="meridia-select-overlay-header"
        v-bind="
          getPt < PassThroughMeridiaMultipleSelect > ('header', passThrough)
        "
      >
        <MeridiaCheckbox
          v-if="!allSelectDisabled"
          :model-value="allSelected"
          :disabled="loading || selectableFilteredOptions.length === 0"
          :size="'small'"
          @update:model-value="clickAll"
          class="pr-xs"
        />
        <MeridiaInput
          v-if="!filteringDisabled"
          v-model="filter"
          placeholder="Search"
          icon="search"
          class="w-full"
          :pt="
            getPt <
            PassThroughMeridiaMultipleSelect >
            ('filterInput', passThrough)
          "
        />
      </div>
    </template>
    <template #option="option">
      <MeridiaCheckbox
        v-model="option.selected"
        :label="option.option.label"
        :disabled="selectRef.isOptionDisabled(option.option)"
        :size="'small'"
        :class="{
          'bg-primary-surface-muted': option.selected,
        }"
        class="p-s cursor-pointer transition-all duration-200 w-full h-full hover:bg-neutral-surface-muted"
        v-bind="
          getPt <
          PassThroughMeridiaMultipleSelect >
          ('meridiaOption', passThrough, option)
        "
        @click.prevent
      >
        <template v-if="slots.option" #label>
          <slot
            name="option"
            :option="option.option"
            :selected="option.selected"
            :disabled="selectRef.isOptionDisabled(option.option)"
            :index="option.index"
          />
        </template>
      </MeridiaCheckbox>
    </template>
    <template #footer="{ value }">
      <div v-if="!clearDisabled" class="flex justify-end p-s">
        <MeridiaButton
          label="Clear"
          :disabled="!value"
          :size="'mini'"
          :type="'tertiary'"
          icon="filter_alt_off"
          class="w-full"
          @click="footerClearClicked"
        />
      </div>
    </template>
    <template #empty>
      <div
        v-if="error"
        class="meridia-select-no-options !text-high-label-default"
      >
        Could not load options. Try again.
      </div>
      <div v-else-if="loading" class="flex flex-col gap-xs p-s">
        <MeridiaSkeleton
          v-for="index in EMPTY_LOADER_ROWS"
          :key="index"
          height="16px"
        />
      </div>
      <div v-else class="meridia-select-no-options">No options found</div>
    </template>
    <template v-if="slots.optiongroup" #optiongroup="option">
      <slot name="optiongroup" :option="option.option" :index="option.index" />
    </template>
  </PrimeMultiSelect>
</template>

<script setup lang="ts" generic="T">
import { computed, Ref, ref, watch } from 'vue';
import {
  SelectSize,
  SelectContent,
  SelectOption,
  SelectCommonProps,
} from '../types';
import { VirtualScrollerLazyEvent } from 'primevue';
import { usePassThrough } from 'primevue/passthrough';
import MeridiaCheckbox from '../../checkbox/MeridiaCheckbox.vue';
import MeridiaInput from '../../inputs/input/MeridiaInput.vue';
import MeridiaButton from '../../button/MeridiaButton.vue';
import MeridiaChip from '../../chip/MeridiaChip.vue';
import MeridiaTag from '../../tag/MeridiaTag.vue';
import _ from 'lodash';
import PrimeMultiSelect, { MultiSelectSlots } from 'primevue/multiselect';
import { useSelectUtils } from '../useSelects';
import { PassThroughMeridiaMultipleSelect } from './types';
import { getPt } from '../../utils';
import MeridiaIcon from '../../icon/MeridiaIcon.vue';
import MeridiaSkeleton from '../../skeleton/MeridiaSkeleton.vue';

const modelValue = defineModel<SelectOption<T>[] | undefined>({
  required: true,
});

const props = withDefaults(
  defineProps<
    {
      options: SelectOption<T>[];
      selectionLimit?: number;
      allSelectDisabled?: boolean;
      /**
       * Renders every loaded option at its natural height instead of the fixed
       * `virtualScrollerOptions.itemSize` row the virtual scroller forces, so a
       * one-line option stays shorter than a wrapped one.
       *
       * `virtualScrollerOptions.onLazyLoad` and `.step` keep driving pagination:
       * the first page is fetched when the overlay opens and the next one when
       * the option list is scrolled near its bottom.
       */
      virtualScrollDisabled?: boolean;
      /** Icon on every chip in `chips` content. Pass `''` for an iconless chip. */
      chipIcon?: string;
      pt?: PassThroughMeridiaMultipleSelect;
    } & SelectCommonProps<T>
  >(),
  {
    size: SelectSize.SMALL,
    disabled: false,
    filteringDisabled: false,
    clearDisabled: false,
    content: SelectContent.TEXT,
    placeholder: 'Select',
    selectionLimit: null,
    allSelectDisabled: false,
    virtualScrollDisabled: false,
    chipIcon: 'visibility',
    appendTo: 'body',
  }
);

const emit = defineEmits<{
  (event: 'update:model-value', value: SelectOption<T>[] | undefined): void;
  (event: 'clear'): void;
  (event: 'focus'): void;
  (event: 'blur'): void;
  (event: 'show'): void;
  (event: 'hide'): void;
  (event: 'option-added', option: SelectOption<T>): void;
  (event: 'option-removed', option: SelectOption<T>): void;
}>();

const slots = defineSlots<
  {
    option: (option: {
      option: SelectOption<T>;
      selected: boolean;
      disabled: boolean;
      index: number;
    }) => any;
    value: (scope: {
      value: SelectOption<T>[] | undefined;
      placeholder: string;
      modelValue: SelectOption<T>[] | undefined;
      setModelValue: (value: SelectOption<T>[] | undefined) => void;
    }) => any;
  } & Pick<MultiSelectSlots, 'optiongroup'>
>();

/** Placeholder rows shown while the first page of options is loading. */
const EMPTY_LOADER_ROWS = 3;

/**
 * Distance (px) from the bottom of the option list at which the next page is
 * requested when `virtualScrollDisabled` replaces the virtual scroller.
 */
const LAZY_LOAD_SCROLL_THRESHOLD = 48;

const sizeClass = computed(() => ({
  'size-small': props.size === SelectSize.SMALL,
  'size-large': props.size === SelectSize.LARGE,
}));

const stateClass = computed(() => ({
  'state-disabled': props.disabled,
  'state-seelected': modelValue.value?.length > 0,
}));

const passThrough = usePassThrough(
  props.pt,
  {
    dropdown: {
      class: 'meridia-select-dropdown-container',
    },
    labelContainer: {
      class: 'h-min flex-grow overflow-hidden',
    },
    label: {
      class: 'meridia-select-placeholder',
    },
    overlay: {
      class: 'meridia-select-overlay',
    },
    pcheadercheckbox: {
      root: {
        class: 'hidden',
      },
    },
    pcOptionCheckbox: {
      root: {
        class: 'hidden',
      },
    },
    listContainer: {
      class:
        'overflow-y-auto scrollbar-thin scrollbar-thumb-slate-400 scrollbar-track-transparent',
      onScroll: onListScroll,
    },
  } as PassThroughMeridiaMultipleSelect,
  { mergeSections: true, mergeProps: true }
) as PassThroughMeridiaMultipleSelect;

const filter = ref('');
const filteredOptions = ref<SelectOption<T>[]>(props.options);

/**
 * The selectable options behind the current filter. With `optionGroupChildren`
 * the filtered list holds group wrappers rather than options, so select-all and
 * the "all selected" state must both read the options nested inside them.
 */
const selectableFilteredOptions = computed<SelectOption<T>[]>(() => {
  const options = filteredOptions.value ?? [];
  const childrenKey = props.optionGroupChildren;
  if (!childrenKey) return options;
  return options.flatMap(
    (group) =>
      ((group as Record<string, unknown>)[childrenKey] as SelectOption<T>[]) ??
      []
  );
});

const allSelected = computed(() => {
  if (selectableFilteredOptions.value.length === 0) return false;
  return _.isEqual(
    _.sortBy(modelValue.value?.map((v) => v.value)),
    _.sortBy(selectableFilteredOptions.value.map((v) => v.value))
  );
});
const loading = ref(false);
const selectRef = ref<InstanceType<typeof PrimeMultiSelect> & any>(null);
const error = ref(false);
const totalCount = ref<number>(props.virtualScrollerOptions?.step ?? 30);

const { virtualLazyLoad, lazyFetchData } = useSelectUtils<T>(
  props,
  filteredOptions as Ref<SelectOption<T>[]>,
  filter,
  totalCount,
  loading,
  error
);

/**
 * PrimeVue virtualises the option list — and pins every row to `itemSize` — as
 * soon as it receives virtual-scroller options, so they are withheld when
 * `virtualScrollDisabled` is set. `onListScroll` takes over the paging the
 * scroller would have driven.
 */
const primeVirtualScrollerOptions = computed(() =>
  props.virtualScrollerOptions && !props.virtualScrollDisabled
    ? {
        ...props.virtualScrollerOptions,
        onLazyLoad: virtualLazyLoad,
        loading: loading.value,
      }
    : undefined
);

/** Event the lazy-load callbacks receive in place of the scroller's own. */
function lazyLoadEvent(): VirtualScrollerLazyEvent {
  const first = filteredOptions.value.length;
  return { first, last: first + (props.virtualScrollerOptions?.step ?? 0) };
}

// Update filtered options when options change
watch(
  () => props.options,
  (options, previousOptions) => {
    // A parent re-render hands over a new array even when nothing changed - an
    // inline `:options="[]"` compiles to a fresh literal every render. Replacing
    // the list on those would discard the options loaded through
    // `asyncFilterFunction` / `onLazyLoad`.
    if (_.isEqual(options, previousOptions)) return;
    filteredOptions.value = [...options];
  }
);

watch(
  modelValue,
  (newValue, oldValue) => {
    // Skip if both are undefined or initial setting
    if (!oldValue && !newValue) return;

    // Initialize empty arrays if undefined
    const newOptions = newValue || [];
    const oldOptions = oldValue || [];

    // Find added options (in newValue but not in oldValue)
    const addedOptions = newOptions.filter(
      (option) => !oldOptions.some((old) => old.value === option.value)
    );

    // Find removed options (in oldValue but not in newValue)
    const removedOptions = oldOptions.filter(
      (option) => !newOptions.some((newOpt) => newOpt.value === option.value)
    );

    // Emit events for each added option
    addedOptions.forEach((option) => {
      emit('option-added', option);
    });

    // Emit events for each removed option
    removedOptions.forEach((option) => {
      emit('option-removed', option);
    });
  },
  { deep: true }
);

/**
 * Without the virtual scroller nothing asks for the first page when the overlay
 * opens, so it is requested here.
 */
function onShow() {
  if (
    props.virtualScrollDisabled &&
    props.virtualScrollerOptions?.onLazyLoad &&
    filteredOptions.value.length === 0
  ) {
    virtualLazyLoad(lazyLoadEvent());
  }
  emit('show');
}

/** Requests the next page once the option list is scrolled near its bottom. */
function onListScroll(event: Event) {
  if (!props.virtualScrollDisabled || !props.virtualScrollerOptions?.onLazyLoad)
    return;
  const list = event.target as HTMLElement;
  const distanceToBottom =
    list.scrollHeight - list.scrollTop - list.clientHeight;
  if (distanceToBottom > LAZY_LOAD_SCROLL_THRESHOLD) return;
  virtualLazyLoad(lazyLoadEvent());
}

function footerClearClicked() {
  selectRef.value.onClearClick();
  selectRef.value.hide();
}

function clickAll(value: boolean) {
  if (value) {
    modelValue.value = selectableFilteredOptions.value;
  } else {
    modelValue.value = [];
  }
}

defineExpose({
  lazyFetchData,
  filteredOptions,
});
</script>

<style src="../select.scss"></style>
